import { useTranslation } from 'react-i18next';
import type { ShipmentItem } from '@/mocks/dashboard';

interface Props {
  shipments: ShipmentItem[];
}

const statusColors: Record<string, string> = {
  pre_transit: 'bg-ink-50 text-ink-400 border-ink-200',
  in_transit: 'bg-warm-50 text-warm-700 border-warm-200',
  out_for_delivery: 'bg-ink-50 text-ink-600 border-ink-200',
  delivered: 'bg-ink-50 text-ink-600 border-ink-200',
  exception: 'bg-warm-50 text-warm-800 border-warm-300',
};

export default function DashboardShipping({ shipments }: Props) {
  const { t } = useTranslation('dashboard');

  return (
    <div className="rounded-xl border border-ink-100 bg-white">
      <div className="flex items-center justify-between px-4 md:px-5 py-3 md:py-4 border-b border-ink-100">
        <div className="flex items-center gap-2">
          <i className="ri-truck-line text-warm-400 text-lg" />
          <h3 className="text-sm md:text-base font-semibold text-ink-600">{t('dashboard.shipping.title')}</h3>
        </div>
        <a href="/my/shipments" className="text-xs md:text-sm font-medium text-warm-400 hover:text-warm-500 transition-colors whitespace-nowrap">
          {t('dashboard.seeAll')}
        </a>
      </div>

      {shipments.length === 0 ? (
        <div className="px-4 md:px-5 py-8 text-center">
          <div className="w-12 h-12 rounded-full bg-cream-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-truck-line text-cream-400 text-xl" />
          </div>
          <p className="text-sm text-ink-300">{t('dashboard.shipping.empty')}</p>
        </div>
      ) : (
        <div className="divide-y divide-ink-100">
          {shipments.map((s) => (
            <div key={s.id} className="px-4 md:px-5 py-3 md:py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-mono text-warm-400">{s.orderNumber}</span>
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${statusColors[s.status]}`}>
                      {t(`dashboard.shipmentStatus.${s.status}`)}
                    </span>
                  </div>
                  <p className="text-xs text-ink-300 mt-1">{s.carrier}</p>
                  {s.trackingNumber && (
                    <p className="text-xs font-mono text-ink-400 mt-0.5">
                      {t('dashboard.shipping.tracking')}: {s.trackingNumber}
                    </p>
                  )}
                  {s.estimatedDelivery && (
                    <p className="text-xs text-ink-300 mt-0.5">
                      {t('dashboard.shipping.delivery')}: {s.estimatedDelivery}
                    </p>
                  )}
                </div>
                <a
                  href={`/my/shipments/${s.id}`}
                  className="shrink-0 text-xs font-medium text-warm-400 hover:text-warm-500 transition-colors whitespace-nowrap mt-0.5"
                >
                  {t('dashboard.shipping.view')}
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}